import { localToolGroups, localTools } from "@pax/environments";
import {
  cn,
  growOrigin,
  MorphPanel,
  OptionRow,
  PanelHeading,
  PanelTile,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DevelopmentTool, DevelopmentToolsVisibility } from "@/development/preferences";
import { localStack } from "@/lib/local-stack";

/**
 * The laptop's other local tools and the page's development tools, behind a chevron
 * beside the logo. Renders nowhere but localhost.
 *
 * The panel is built like the game's control center: the same heading, and a
 * developer tools tile that grows in place into its own pane. It sits over a
 * page rather than a map, so it wears the solid surface.
 *
 * It hangs off the frame rather than off the chevron, because the head is
 * clipped to the column and the brand travels on a translate that would pin
 * a fixed panel inside that clip.
 */

type View = "apps" | "tools";

const TOOLS: { tool: DevelopmentTool; name: string; description: string }[] = [
  {
    tool: "botChecks",
    name: "Bot checks",
    description: "Inspect verification and queue Castle's next verdict.",
  },
  {
    tool: "tanstackDevtools",
    name: "TanStack Devtools",
    description: "Session, queries, and routes in one panel.",
  },
  {
    tool: "reactGrab",
    name: "React Grab",
    description: "Copy an element's source context for an agent.",
  },
  {
    tool: "reactScan",
    name: "React Scan",
    description: "Outlines components as they render.",
  },
  {
    tool: "viteDevtools",
    name: "Vite DevTools",
    description: "Modules, plugins, transforms, and terminals.",
  },
];

type LocalMenuProps = {
  developmentTools: DevelopmentToolsVisibility;
  onDevelopmentToolVisibilityChange: (tool: DevelopmentTool, visible: boolean) => void;
};

export function LocalMenu({ developmentTools, onDevelopmentToolVisibilityChange }: LocalMenuProps) {
  if (!import.meta.env.DEV || !window.location.hostname.endsWith("localhost")) return null;
  return (
    <LocalPanel
      developmentTools={developmentTools}
      onDevelopmentToolVisibilityChange={onDevelopmentToolVisibilityChange}
    />
  );
}

function LocalPanel({ developmentTools, onDevelopmentToolVisibilityChange }: LocalMenuProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("apps");
  const [growFrom, setGrowFrom] = useState("center");
  const paneRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setView("apps");
  }, []);

  const panelRef = usePanelDismiss<HTMLDivElement>({
    open,
    onDismiss: close,
    trigger: triggerRef,
  });

  // Listing the app you are already in would be a link to nowhere.
  const tools = localTools(localStack).filter(
    tool => new URL(tool.href).host !== window.location.host,
  );
  const toolsOn = TOOLS.filter(({ tool }) => developmentTools[tool]).map(({ name }) => name);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Local tools"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(
          "rounded-md p-1 text-muted-foreground transition hover:text-foreground",
          open && "text-foreground",
        )}
      >
        <ChevronDown className="size-3.5" aria-hidden />
      </button>

      {open &&
        createPortal(
          <MorphPanel
            ref={panelRef}
            view={view}
            anchor="top-left"
            growFrom={growFrom}
            paneRef={paneRef}
            className={cn(
              "overlay-panel fixed top-(--bar-height) left-3 z-50 text-sm",
              solidSurface,
            )}
          >
            <div className="w-[18rem] p-2">
              <PanelHeading
                label={view === "apps" ? "Local" : "Web tools"}
                onBack={view === "tools" ? () => setView("apps") : undefined}
                onClose={close}
              />
              {view === "apps" ? (
                <div className="flex flex-col gap-2">
                  {localToolGroups.map(group => (
                    <div key={group} className="flex flex-col gap-0.5">
                      <p className="px-2 pt-1 text-xs font-medium text-muted-foreground">{group}</p>
                      {tools
                        .filter(tool => tool.group === group)
                        .map(tool => (
                          <a
                            key={tool.href}
                            href={tool.href}
                            target="_blank"
                            rel="noreferrer"
                            onClick={close}
                            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition hover:bg-foreground/10 active:scale-[0.99]"
                          >
                            <span className="min-w-0 flex-1 truncate">{tool.label}</span>
                            <ArrowUpRight
                              aria-hidden
                              className="size-4 shrink-0 text-muted-foreground"
                            />
                          </a>
                        ))}
                    </div>
                  ))}
                  <PanelTile
                    label="Web tools"
                    onOpen={tile => {
                      setGrowFrom(growOrigin(paneRef.current, tile));
                      setView("tools");
                    }}
                  >
                    <span className="text-sm font-medium">
                      {toolsOn.length === 0 ? "All off" : toolsOn.join(" · ")}
                    </span>
                  </PanelTile>
                </div>
              ) : (
                <div className="flex flex-col gap-0.5 rounded-xl bg-foreground/5 p-2">
                  {TOOLS.map(({ tool, name, description }) => (
                    <OptionRow
                      key={tool}
                      selected={developmentTools[tool]}
                      onSelect={() =>
                        onDevelopmentToolVisibilityChange(tool, !developmentTools[tool])
                      }
                      name={name}
                      description={description}
                    />
                  ))}
                </div>
              )}
            </div>
          </MorphPanel>,
          document.body,
        )}
    </>
  );
}
